import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import Modal, { Field, TextInput, PrimaryButton, SecondaryButton, GhostButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Copy, Mail } from "lucide-react";

const MODES = { invite: "invite", password: "password", none: "none" };
const initial = {
  name: "", email: "", phone: "", external_customer_id: "", address: "",
  mode: MODES.invite, portal_password: "Customer@123",
};

function inviteUrlFor(token) {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/invite/${token}`;
}

export default function Customers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [inviteModal, setInviteModal] = useState(null); // { url, name }

  const load = async () => setItems((await api.get("/company/customers")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      external_customer_id: form.external_customer_id,
      address: form.address,
      send_invite: form.mode === MODES.invite,
      create_portal_account: form.mode === MODES.password,
      portal_password: form.mode === MODES.password ? form.portal_password : undefined,
    };
    try {
      const { data } = await api.post("/company/customers", payload);
      setOpen(false);
      setForm(initial);
      load();
      if (data.invite_token) {
        setInviteModal({ url: inviteUrlFor(data.invite_token), name: data.name });
      } else {
        toast.success("Customer created");
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendInvite = async (customer) => {
    try {
      const { data } = await api.post(`/company/customers/${customer.id}/invite`);
      setInviteModal({ url: inviteUrlFor(data.invite_token), name: customer.name });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteModal.url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <div className="space-y-8" data-testid="company-customers-page">
      <PageHeader
        eyebrow="People"
        title="Customers"
        description="Buyers with portal access mapped to purchased units."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-customer-button">+ New Customer</PrimaryButton>}
      />

      <DataTable
        testId="customers-table"
        columns={[
          { key: "name", label: "Name", render: (r) => <div className="font-bold">{r.name}</div> },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "external_customer_id", label: "External ID", render: (r) => <span className="font-mono text-xs">{r.external_customer_id || "—"}</span> },
          { key: "ownerships_count", label: "Units" },
          {
            key: "access",
            label: "Access",
            render: (r) => r.user_id ? <StatusBadge value="active" label="Portal Active" /> : <StatusBadge value="pending" label="Invite Pending" />,
          },
          { key: "created_at", label: "Added", render: (r) => fmtDate(r.created_at) },
          {
            key: "actions",
            label: "Actions",
            render: (r) =>
              !r.user_id ? (
                <GhostButton onClick={() => sendInvite(r)} testId={`send-invite-${r.id}`}>
                  <Mail size={12} className="inline mr-1" /> Send invite
                </GhostButton>
              ) : null,
          },
        ]}
        rows={items}
        empty="No customers yet."
      />

      <Modal open={open} onClose={() => setOpen(false)} title="New customer" testId="new-customer-modal">
        <form onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name"><TextInput testId="form-cust-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email"><TextInput testId="form-cust-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone"><TextInput testId="form-cust-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="External customer ID"><TextInput testId="form-cust-ext-id" placeholder="ERP / CRM id" value={form.external_customer_id} onChange={(e) => setForm({ ...form, external_customer_id: e.target.value })} /></Field>
          </div>
          <Field label="Address"><TextInput testId="form-cust-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>

          <div className="border-t border-stone-200 pt-4">
            <div className="overline text-stone-500 mb-3">Portal access</div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { key: MODES.invite, label: "Send invite link (customer sets own password)", tag: "RECOMMENDED" },
                { key: MODES.password, label: "Create account with temp password" },
                { key: MODES.none, label: "No portal access (create record only)" },
              ].map((opt) => (
                <label
                  key={opt.key}
                  className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                    form.mode === opt.key ? "border-brand-500 bg-brand-50" : "border-stone-200 hover:bg-stone-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="access-mode"
                    value={opt.key}
                    checked={form.mode === opt.key}
                    onChange={() => setForm({ ...form, mode: opt.key })}
                    data-testid={`access-mode-${opt.key}`}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-stone-950">{opt.label}</div>
                      {opt.tag && <span className="text-[10px] tracking-wider font-bold text-brand-700 bg-brand-100 px-1.5 py-0.5">{opt.tag}</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {form.mode === MODES.password && (
              <Field label="Temporary password"><TextInput testId="form-cust-portal-pwd" value={form.portal_password} onChange={(e) => setForm({ ...form, portal_password: e.target.value })} /></Field>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-customer">Create</PrimaryButton>
          </div>
        </form>
      </Modal>

      <Modal open={!!inviteModal} onClose={() => setInviteModal(null)} title="Invite link ready" testId="invite-link-modal">
        {inviteModal && (
          <div>
            <p className="text-sm text-stone-600 mb-3">
              Share this link with <span className="font-bold text-stone-950">{inviteModal.name}</span>. It expires in 14 days.
            </p>
            <div className="border border-stone-300 bg-stone-50 p-3 font-mono text-xs break-all" data-testid="invite-link-text">
              {inviteModal.url}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <SecondaryButton type="button" onClick={() => setInviteModal(null)}>Close</SecondaryButton>
              <PrimaryButton type="button" onClick={copyInvite} testId="copy-invite-link">
                <Copy size={14} className="inline mr-1" /> Copy link
              </PrimaryButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
